import { Link } from "react-router-dom";
import {
  PendingDecision,
  PolicyDocument,
  PolicyList,
  PolicySection,
  PolicySubheading,
  PolicyText,
} from "@/components/PolicyDocument";

/**
 * The privacy policy.
 *
 * EVERY CLAIM HERE WAS READ OFF THE SCHEMA AND THE CODE, not off an
 * understanding of what the product is supposed to do. That distinction is the
 * whole point of the document: a policy describing assumed data is as wrong as
 * one that omits real data, and both are worse than saying "not decided yet".
 *
 * If you change what Pickwell stores, this page changes in the same PR. The
 * sources of truth, in order:
 *   * migrations/*.sql — what columns exist and what the privacy rules on them
 *     are (0003's "costs and behavior, never content" rule, 0005's note that
 *     `unmet_requests` deliberately holds verbatim search text, 0008's
 *     `gateway_log_id` on `session_events` and `build_events`).
 *   * functions/lib/category-builder.ts — writes `generated_categories
 *     .source_need`, the one verbatim store with no account column, AND
 *     `categoryIdFromNeed`, which is not an opaque classification: it is the
 *     first four non-framing words the shopper typed, hyphen-joined. That value
 *     is `sessions.category` (attached to `user_id`), `generated_categories.id`
 *     and `build_events.category_id` (neither attributed, neither cascaded).
 *     A policy that calls it "a derived label" and stops there is wrong.
 *   * functions/lib/failure-log.ts — the closed `FailureContext` whitelist and
 *     the two free-text columns (`message`, `stack`) whose contents are authored
 *     by whatever threw. Migration 0009 states the limit of what can be promised
 *     about those two, and the "When something goes wrong" copy below is that
 *     statement in plain words — including the part we have NOT verified. It is
 *     also the only table with an automatic expiry, which the retention section
 *     has to keep saying for as long as that stays true.
 *   * docs/ai-gateway-setup.md — the gateway's logging and cache posture, which
 *     is the largest gap between "what our database keeps" and "what is kept
 *     about a shopper". `session_events.gateway_log_id` is the join between the
 *     two: an account-attached pointer into logs that do hold content.
 *
 * Revision history and the open human decisions: docs/policy-revisions.md.
 */
export function PrivacyPage() {
  return (
    <PolicyDocument title="Privacy" reviewed="2026-09-24">
      <PolicyText>
        This describes what Pickwell stores, what leaves our systems, and who else sees it. It
        describes the product as it is built today, not as it is planned. Where something has not
        been decided, it says so rather than guessing.
      </PolicyText>

      <PolicySection id="short-version" heading="The short version">
        <PolicyList>
          <li>
            We store your account, the budget range you gave, what each search cost us to run, and a
            short label for the category you shopped — which, for most searches, is built out of the
            words you typed.
          </li>
          <li>
            We do <strong>not</strong> store the questions we asked you or the answers you gave.
          </li>
          <li>
            Your typed words survive in <strong>three</strong> forms described below: two verbatim
            copies, and that shortened category label. Several of those copies carry no account link
            at all and are kept indefinitely, which means deleting your account would not reach
            them.
          </li>
          <li>
            Everything runs on Cloudflare, and the AI gateway your text passes through keeps a log
            of that text — including in the cases where our own database deliberately does not.
          </li>
          <li>
            When a request fails we keep a technical record of the failure for 30 days, with no
            account attached, so we can fix it.
          </li>
          <li>
            We build no profile of you, take no payments, and load no advertising or analytics
            trackers.
          </li>
        </PolicyList>
      </PolicySection>

      <PolicySection id="what-we-store" heading="What we store">
        <PolicySubheading>Your account</PolicySubheading>
        <PolicyText>
          Your email address and name. If you registered with a password, a hash of that password —
          never the password itself. If you signed in with Google, the stable account identifier
          Google issues for you (not your email address), plus the name and email address Google
          gives us. One flag recording whether the account is an administrator.
        </PolicyText>

        <PolicySubheading>Your sign-in</PolicySubheading>
        <PolicyText>
          A sign-in session is an identifier and an expiry time, nothing else. Logging out deletes
          it. Expired ones are cleaned up.
        </PolicyText>

        <PolicySubheading>Your shopping sessions</PolicySubheading>
        <PolicyText>
          For each session we keep the category you shopped, the budget range you gave, the fee we
          quoted, whether we waived it, whether you went ahead after seeing it, when the search
          finished, and whether you clicked through to Amazon.
        </PolicyText>
        <PolicyText>
          The category is stored as a short label rather than a sentence — but for most searches{" "}
          <strong>that label is built out of your own words</strong>, not picked from a fixed list,
          and it sits on the session row attached to your account. How it is built, and what follows
          from that, is set out under “Where your typed words survive” below.
        </PolicyText>
        <PolicyText>
          We do not keep the questions we asked, the answers you gave, or the products we showed
          you. That is a rule written into the database schema itself, not an accident of how it
          happens to be implemented today. It covers the questions and the answers; it does not make
          the category label content-free, because that label is made by deleting words from your
          request rather than by classifying it.
        </PolicyText>

        <PolicySubheading>What a search cost us to run</PolicySubheading>
        <PolicyText>
          One row per model call and per product-data draw: a cost figure, whether a cached response
          was used, which model answered, how many tokens moved, and the identifier Cloudflare's AI
          gateway assigned to that call. No content of any kind — but that last item is the address
          of the gateway's own log entry for the call, and those entries do carry your text. “Who
          else sees it” below says what that means. The work of building a new category is recorded
          the same way, against the category label described below, with no link to any account.
        </PolicyText>

        <PolicySubheading>When something goes wrong</PolicySubheading>
        <PolicyText>
          When a request fails, we keep a record of the failure for 30 days so we can find and fix
          it: which part of the system broke, the error it produced and its stack trace, the
          category label described below, the path you requested, the status you got back, and
          identifiers for the request and for the version of the software that was running. There is{" "}
          <strong>no account attached</strong>, so an account deletion does not reach these rows,
          and we could not reliably find yours if you asked. After 30 days they are deleted
          automatically — the only data in Pickwell that expires on its own.
        </PolicyText>
        <PolicyText>
          One honest caveat. The error text and stack trace are written by whatever broke — our own
          code, Cloudflare's runtime, or the model provider we called. We do not put your typed
          words into them, and we do not believe any provider we use echoes your text back inside an
          error message, but we have not verified that for every provider and every failure. So the
          accurate description is that these are usually machine error text, and are in principle
          capable of quoting a fragment of what you typed. They are readable only by us.
        </PolicyText>

        <PolicySubheading>Where your typed words survive</PolicySubheading>
        <PolicyText>
          A stated need can carry far more about you than a product name does — “something for my
          mother's chemo nausea” is a medical fact about a third person. Storing it is defensible;
          storing it without saying so is not. Your words survive in three forms:
        </PolicyText>
        <PolicyList>
          <li>
            <strong>When we cannot cover what you asked for.</strong> We keep what you typed,
            exactly as typed, attached to your account, together with which of the categories we do
            cover you picked instead. This is the list we use to decide what to build next.
          </li>
          <li>
            <strong>When your request causes us to build a new category.</strong> Up to the first
            500 characters of what you typed is stored verbatim alongside the category it produced,
            as the record of what that category was built to answer.{" "}
            <strong>This copy has no account attached to it.</strong> It is kept indefinitely, and
            it is reused — the category built from your words serves every later shopper who asks
            for the same thing. Because nothing links it back to you, deleting your account would
            not remove it, and we could not reliably find it if you asked us to.
          </li>
          <li>
            <strong>Shortened into the category label, on every search.</strong> The label is not a
            code from a fixed taxonomy. We lower-case what you typed, drop punctuation, remove a
            short list of framing words (“I”, “need”, “for”, “best”), and join the first four words
            that survive with hyphens — so the example above is stored as{" "}
            <strong>something-mothers-chemo-nausea</strong>. That label goes three places: onto your
            shopping session <strong>attached to your account</strong>, into the permanent
            identifier of the category we built, and onto every cost record for building it. The
            last two carry no account link and no expiry, so — like the verbatim copy above — an
            account deletion does not reach them.
          </li>
        </PolicyList>
        <PolicyText>
          Stored alongside that second copy is the evidence for the category we built: the names of
          the Amazon browse categories we probed and a sample of the product titles we read back.
          That is catalogue data about products, not about you.
        </PolicyText>
      </PolicySection>

      <PolicySection id="what-we-do-not-do" heading="What we do not do">
        <PolicyList>
          <li>
            <strong>We do not build a profile of you.</strong> Nothing carries what we learned in
            one session into the next. The database has a table designed to hold durable facts about
            a shopper, and no code writes to it. If that changes, this page changes first.
          </li>
          <li>
            <strong>We take no payments.</strong> No payment processor is connected to Pickwell. We
            quote a fee for each search and then waive it. We hold no card details, and no payment
            company receives anything about you from us.
          </li>
          <li>
            <strong>We run no advertising or analytics trackers.</strong> No third-party script
            loads in the app.
          </li>
          <li>
            <strong>We do not sell or rent your data</strong>, and we send nothing about you to a
            data broker or an advertising network.
          </li>
          <li>
            <strong>We do not learn what you bought.</strong> Affiliate reporting arrives in
            aggregate and lags by days; nothing in Pickwell records whether you personally purchased
            anything. What we record is that a link was clicked.
          </li>
        </PolicyList>
      </PolicySection>

      <PolicySection id="third-parties" heading="Who else sees it">
        <PolicySubheading>
          Cloudflare — all of it, and more than our database keeps
        </PolicySubheading>
        <PolicyText>
          Pickwell runs entirely on Cloudflare: the application, the database, and the gateway every
          model call passes through. Cloudflare's platform therefore handles everything described
          above.
        </PolicyText>
        <PolicyText>
          It also holds something our own database does not.{" "}
          <strong>
            The AI gateway has request logging switched on and caches responses for an hour, and
            those logs carry request and response content
          </strong>{" "}
          — which means the text you typed and the answers you gave are retained by Cloudflare even
          in the cases where Pickwell deliberately stores neither. If you read one paragraph on this
          page, read this one.
        </PolicyText>
        <PolicyText>
          The two halves are joined, and we would rather state the join than leave you to infer it.{" "}
          <strong>
            Our cost records store the identifier of each gateway log entry, alongside the session
            and therefore alongside your account.
          </strong>{" "}
          An identifier is not content, but it is the address of an entry that is — it is what would
          pick your particular request out of Cloudflare's logs. Deleting your account deletes our
          copy of that pointer; it does not reach Cloudflare's copy of the log. The cost records for
          building a category carry the same identifiers with no account link at all.
        </PolicyText>
        <PendingDecision id="gateway-log-retention">
          How long those gateway logs are retained, and whether Pickwell should switch content
          logging off, has not been decided. The retention window is currently Cloudflare's default
          rather than a choice we made.
        </PendingDecision>

        <PolicySubheading>The model that reads your text</PolicySubheading>
        <PolicyText>
          Your typed need and your free-text answers are sent to a language model, which turns them
          into structured requirements. Today that model runs on Cloudflare's own infrastructure
          rather than at an outside provider, and the call still passes through the gateway
          described above. Pickwell can also be configured to route to a third-party model provider
          through that same gateway; where it is, that provider receives the same text. Which
          provider served a session is recorded — by name, alongside the cost, never with your text.
        </PolicyText>

        <PolicySubheading>Keepa — never your words</PolicySubheading>
        <PolicyText>
          Product and listing-history data comes from Keepa. What Keepa receives is a compiled
          query: category identifiers, keyword terms derived from your request, numeric filters
          built from your budget and requirements, and product identifiers. It does not receive what
          you typed, the questions we asked, or the answers you gave, and nothing in the query
          identifies you.
        </PolicyText>

        <PolicySubheading>Google — only if you use it</PolicySubheading>
        <PolicyText>
          If you sign in with Google, Google tells us your account identifier, your name and your
          email address. We send Google nothing about your shopping.
        </PolicyText>

        <PolicySubheading>Amazon — when you click</PolicySubheading>
        <PolicyText>
          Product links go to Amazon carrying our Associates tag. Following one is a visit to
          Amazon, covered by Amazon's own privacy notice, and Amazon sets its attribution cookie on
          its own side. We record that a click happened. Amazon does not tell us who bought what.
        </PolicyText>
      </PolicySection>

      <PolicySection id="cookies" heading="Cookies and local storage">
        <PolicyText>
          One cookie: your sign-in session. There are no advertising cookies and no analytics
          cookies. Your light/dark preference is kept in your browser's local storage and is never
          sent to us. The 24-hour attribution cookie that credits Pickwell with a purchase is set by
          Amazon when you arrive there, not by us.
        </PolicyText>
      </PolicySection>

      <PolicySection id="signed-out" heading="If you are not signed in">
        <PolicyText>
          Searching requires an account today. A search spends metered data credits from a shared
          budget, so it sits behind a sign-in. A signed-out visitor can read this page, the terms,
          and the home page; we store nothing about that visit beyond the ordinary request handling
          of the platform we run on.
        </PolicyText>
        <PolicyText>
          One nuance is worth stating now rather than later: the verbatim category text described
          above, and the category label built from your words, already carry no account on the
          copies held against the category itself — even though every search that produces them
          comes from a signed-in shopper. If Pickwell later allows searching without an account,
          this is the section that changes.
        </PolicyText>
      </PolicySection>

      <PolicySection id="retention" heading="How long we keep it">
        <PendingDecision id="retention">
          Pickwell has no retention schedule. Deciding one — how long search text, shopping sessions
          and cost records should live — is a business decision that has not been made, and this
          page will not invent a number for it.
        </PendingDecision>
        <PolicyText>
          What is true today: sign-in sessions expire and are deleted. Shopping sessions carry a
          seven-day expiry, after which they are treated as expired — but the rows are not removed.
          Account data, cost records and the search text described above are kept until someone
          deletes them. The un-attributed category text, and the category label built from your
          words, are kept indefinitely by design, because later shoppers are served from them.
        </PolicyText>
        <PolicyText>
          The one exception, and the only automatic deletion in the product besides sign-in
          sessions: the failure records described above are deleted after 30 days.
        </PolicyText>
      </PolicySection>

      <PolicySection id="access-and-deletion" heading="Seeing, correcting or deleting your data">
        <PolicyText>
          Said plainly: there is no self-serve export and no delete button. Logging out deletes your
          sign-in session, and that is the only deletion the product performs on its own. Everything
          else is a manual request that a person handles.
        </PolicyText>
        <PolicyText>
          If your account is deleted, the shopping sessions and cost records attached to it are
          deleted with it — including the category labels on those sessions and the gateway log
          identifiers on those cost records. The search text we kept because we could not cover your
          request would have its account link removed rather than being deleted by that cascade — so
          we would delete those rows as part of the same request, while the link still exists to
          find them by.
        </PolicyText>
        <PolicyText>
          Three things would remain, and we would rather name them than let the word “deleted” do
          more work than it can: the verbatim category text with no account attached; the category
          label built from your words, which is the permanent identifier of the category itself and
          is stamped on build cost records that no account cascade reaches; and Cloudflare's gateway
          logs, which hold your text and are not ours to delete.
        </PolicyText>
        <PendingDecision id="contact-and-deletion-path">
          Where to send an access or deletion request has not been decided — there is no support
          address to publish yet. Nor has whether Pickwell commits to a self-serve deletion path,
          and by when. Until both are settled this page cannot tell you how to reach us, and it will
          not pretend otherwise.
        </PendingDecision>
      </PolicySection>

      <PolicySection id="gaps" heading="What this document does not cover yet">
        <PendingDecision id="entity">
          Pickwell is a 2amlogic property. The legal entity behind it, its registered address, and
          the law governing this document are not settled, so this page does not name them.
        </PendingDecision>
        <PolicyText>
          This was written by the people who built Pickwell, from its database schema and its source
          code, and it has not been reviewed by a lawyer. Sections such a review would normally add
          are deliberately absent rather than quietly assumed: children's data, international
          transfers, the specific rights of readers in the EU, the UK or California, a named
          data-protection contact, and a route to complain to a supervisory authority.
        </PolicyText>
      </PolicySection>

      <PolicySection id="changes" heading="Changes">
        <PolicyText>
          When what Pickwell stores changes, this page changes in the same release. See also the{" "}
          <Link to="/terms" className="underline underline-offset-4 hover:text-foreground">
            terms of service
          </Link>
          , which covers how we are paid and what we promise about recommendations.
        </PolicyText>
      </PolicySection>
    </PolicyDocument>
  );
}
